import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import NoRecordFound from '../../../components/common/no-record-found';
import { formatDateDDMMYY } from '../../../components/common/dateUtils';

const PaymentCollectionReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/api/report/payment-collection-report`, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to fetch');
        const result = await res.json();
        setData(result.data || []);
      } catch (err) {
        setError('Failed to load payment collection report');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="max-w-8xl mx-auto p-6 bg-white shadow-sm border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Payment Collection Report</h2>
          <p className="text-sm text-gray-500 mt-1">Overview of payments received against invoices</p>
        </div>
      </div>
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
          <p className="text-sm text-gray-600">Loading payment collection data...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
          <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      ) : data.length === 0 ? (
        <NoRecordFound message="No payment records found" subMessage="No data available for the selected criteria" iconClass="text-gray-400" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-xs">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-gray-700 font-semibold">
              <tr>
                <th className="px-4 py-2 text-left">Buyer</th>
                <th className="px-4 py-2 text-left">Sales Contract No</th>
                <th className="px-4 py-2 text-left">Sales Order No</th>
                <th className="px-4 py-2 text-left">Invoice No</th>
                <th className="px-4 py-2 text-right">Invoice Amount</th>
                <th className="px-4 py-2 text-right">Payment Received</th>
                <th className="px-4 py-2 text-left">Date Received</th>
                <th className="px-4 py-2 text-left">Payment Method</th>
                <th className="px-4 py-2 text-right">Balance Due</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {data.map((row, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-3">{row.buyer}</td>
                  <td className="px-4 py-3">{row.sales_contract_no}</td>
                  <td className="px-4 py-3">{row.sales_order_no}</td>
                  <td className="px-4 py-3">{row.invoice_no}</td>
                  <td className="px-4 py-3 text-right font-mono">{row.invoice_amount}</td>
                  <td className="px-4 py-3 text-right font-mono">{row.payment_received}</td>
                  <td className="px-4 py-3">{formatDateDDMMYY(row.date_received)}</td>
                  <td className="px-4 py-3">{row.payment_method}</td>
                  <td className="px-4 py-3 text-right font-mono">{row.balance_due}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PaymentCollectionReport;